import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAccess } from "@/lib/access";
import { toast } from "sonner";
import {
  ClipboardText,
  Plus,
  Warning,
  CheckCircle,
  X,
  Clock,
  PaperPlaneTilt,
  FileText,
} from "@phosphor-icons/react";
import RecipeCombobox from "@/components/RecipeCombobox";
import InventoryItemCombobox from "@/components/InventoryItemCombobox";

/**
 * Production Requisitions — the request/approve handshake between the
 * Production team and the Store team.
 *
 * Roles seen by this page:
 *   - `production` (or admin/inventory): can Create + Submit + Cancel own drafts
 *   - `store`      (or admin/inventory): can Approve / Reject submitted requisitions
 * Strict gating happens on the backend; this UI just hides buttons the user
 * cannot use to keep the surface honest.
 */

const STATUS_META = {
  draft: { label: "Draft", tone: "bg-slate-100 text-slate-600 border-slate-200", icon: FileText },
  submitted: { label: "Awaiting Store", tone: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  approved: { label: "Approved · Reserved", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle },
  rejected: { label: "Rejected", tone: "bg-rose-50 text-rose-700 border-rose-200", icon: X },
  cancelled: { label: "Cancelled", tone: "bg-slate-100 text-slate-500 border-slate-200", icon: X },
  fulfilled: { label: "Fulfilled", tone: "bg-sky-50 text-sky-700 border-sky-200", icon: CheckCircle },
};

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "submitted", label: "Awaiting Store" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export function useTeamRoles() {
  const { roles = [] } = useAccess();
  const r = new Set(roles);
  const isAdmin = r.has("admin") || r.has("inventory");
  return {
    canCreate: isAdmin || r.has("production"),
    canApprove: isAdmin || r.has("store"),
    isAdmin,
    roles,
  };
}

export default function Requisitions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const { canCreate } = useTeamRoles();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inventory/requisitions${filter ? `?status=${filter}` : ""}`);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requisitions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { draft: 0, submitted: 0, approved: 0, rejected: 0, cancelled: 0 };
    rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="inv-requisitions-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            Production ↔ Store Handshake
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-1">Production Requisitions</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Production team raises a requisition · Store team verifies inventory and
            approves — approval auto-reserves stock and opens the production run.
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition shadow-sm"
            data-testid="req-new-btn"
          >
            <Plus size={14} weight="bold" /> New Requisition
          </button>
        )}
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={`h-8 px-3 rounded-full border text-xs font-semibold transition ${
              filter === f.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`req-filter-${f.key || "all"}`}
          >
            {f.label}
            {f.key && counts[f.key] > 0 && (
              <span className="ml-1.5 opacity-70">· {counts[f.key]}</span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Recipe</th>
              <th className="px-4 py-3">Batch</th>
              <th className="px-4 py-3">Requested By</th>
              <th className="px-4 py-3">Requested</th>
              <th className="px-4 py-3">Target Date</th>
              <th className="px-4 py-3">Shortage</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400 text-sm">Loading requisitions…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center">
                <ClipboardText size={36} weight="duotone" className="mx-auto text-slate-300" />
                <div className="mt-2 text-sm text-slate-500">
                  {filter ? `No requisitions in "${filter}" status.` : "No requisitions yet."}
                </div>
                {canCreate && !filter && (
                  <button
                    onClick={() => setCreating(true)}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900"
                    data-testid="req-empty-create-btn"
                  >
                    <Plus size={12} weight="bold" /> Create the first requisition
                  </button>
                )}
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.draft;
              const Icon = meta.icon;
              const short = Number(r.total_shortage_at_request || 0);
              return (
                <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition" data-testid={`req-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{r.code}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.recipe_name}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">v{r.recipe_version}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{r.batch_size} kg</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{r.requested_by_name || r.requested_by_email}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{(r.requested_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.target_production_date || "—"}</td>
                  <td className="px-4 py-3">
                    {short > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700">
                        <Warning size={12} weight="fill" /> {short.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-xs text-emerald-600 font-medium">OK</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border ${meta.tone}`}>
                      <Icon size={10} weight="fill" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/inventory/requisitions/${r.id}`}
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-900"
                      data-testid={`req-open-${r.id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && <NewRequisitionModal onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}

function NewRequisitionModal({ onClose, onCreated }) {
  const [recipeId, setRecipeId] = useState("");
  const [batchSize, setBatchSize] = useState(10);
  const [fgItemId, setFgItemId] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitDirectly, setSubmitDirectly] = useState(true);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const canSave = recipeId && batchSize > 0;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/requisitions", {
        recipe_id: recipeId,
        batch_size: Number(batchSize),
        fg_item_id: fgItemId || "",
        target_production_date: targetDate || "",
        notes: notes || "",
        submit: submitDirectly,
      });
      toast.success(
        submitDirectly
          ? `${data.code} submitted to Store team`
          : `${data.code} saved as draft`,
      );
      onClose();
      onCreated?.();
      navigate(`/inventory/requisitions/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create requisition");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="req-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="duotone" className="text-emerald-700" />
            <h2 className="font-heading text-base font-bold">New Production Requisition</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="req-modal-close">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <Field label="Recipe *">
            <RecipeCombobox value={recipeId} onChange={setRecipeId} testId="req-recipe" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Batch Size (kg) *">
              <input
                type="number"
                min="0"
                step="0.01"
                value={batchSize}
                onChange={(e) => setBatchSize(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                data-testid="req-batch-size"
              />
            </Field>
            <Field label="Target Production Date">
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                data-testid="req-target-date"
              />
            </Field>
          </div>
          <Field label="Finished-Good Item (optional)">
            <InventoryItemCombobox value={fgItemId} onChange={setFgItemId} testId="req-fg-item" />
          </Field>
          <Field label="Notes to Store team">
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any priority, allergen note, packaging request…"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              data-testid="req-notes"
            />
          </Field>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={submitDirectly}
              onChange={(e) => setSubmitDirectly(e.target.checked)}
              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              data-testid="req-submit-directly"
            />
            Submit to Store team immediately (uncheck to save as draft)
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-white"
            data-testid="req-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave || busy}
            className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="req-modal-save"
          >
            {busy ? "Saving…" : submitDirectly ? "Create & Submit" : "Save Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
